/**
 * Shared image-to-video configuration for the Sogni Vibe frontend and server.
 *
 * Keep this module browser-safe: it intentionally has no Node-only imports and
 * performs validation without coercing or clamping caller-provided values.
 */

function freezeArray(values) {
  return Object.freeze([...values]);
}

function freezeObject(value) {
  return Object.freeze({ ...value });
}

function hasOwn(object, key) {
  return Object.prototype.hasOwnProperty.call(object, key);
}

export class VideoConfigError extends Error {
  constructor(message, { code = 'invalid_video_config', field } = {}) {
    super(message);
    this.name = 'VideoConfigError';
    this.code = code;
    this.field = field;
  }
}

function fail(field, message, code) {
  throw new VideoConfigError(message, { field, code });
}

const WAN_FPS = freezeArray([16, 32]);
const LTX_FPS = freezeArray([24]);

function audioFamily(id, label, min, max, dimensions, extra = {}) {
  return freezeObject({ id, label, supportsNativeAudio: true,
    durationSeconds: freezeObject({ min, max, default: 6 }),
    fps: freezeObject({ allowed: LTX_FPS, default: 24, generation: 24 }),
    dimensions: freezeObject(dimensions), ...extra });
}

export const VIDEO_FAMILIES = Object.freeze({
  wan22: freezeObject({
    id: 'wan22',
    label: 'WAN 2.2',
    description: 'Reliable image animation with optional 32 fps smoothing.',
    supportsNativeAudio: false,
    durationSeconds: freezeObject({ min: 1, max: 10, default: 5 }),
    fps: freezeObject({ allowed: WAN_FPS, default: 16, generation: 16 }),
    dimensions: freezeObject({ min: 480, max: 1536, divisor: 16 }),
    frames: freezeObject({ min: 17, max: 161, step: 1 })
  }),
  ltx23: freezeObject({
    id: 'ltx23',
    label: 'LTX-2.3',
    description: 'Longer native 24 fps clips with synchronized audio.',
    supportsNativeAudio: true,
    durationSeconds: freezeObject({ min: 4, max: 20, default: 5 }),
    fps: freezeObject({ allowed: LTX_FPS, default: 24, generation: 24 }),
    dimensions: freezeObject({ min: 640, max: 3840, divisor: 8 }),
    frames: freezeObject({ min: 97, max: 481, step: 8 })
  }),
  ltx25: audioFamily('ltx25', 'LTX-2.5', 2, 20, { min: 640, max: 3840, divisor: 8 }, {
    frames: freezeObject({ min: 25, max: 505, step: 8 })
  }),
  minimax: audioFamily('minimax', 'MiniMax H3', 5, 15, { min: 480, max: 1344, divisor: 32 }, {
    frames: freezeObject({ min: 124, max: 362, step: 17 }), maxPixels: 1032192
  }),
  seedance: audioFamily('seedance', 'Seedance', 4, 30, { min: 480, max: 3840, divisor: 1 }, { partner: true }),
  happyhorse: audioFamily('happyhorse', 'HappyHorse 1.1', 3, 15, { min: 480, max: 2560, divisor: 1 }, { partner: true }),
  wan3: audioFamily('wan3', 'Wan 3', 2, 30, { min: 480, max: 1920, divisor: 1 }, {
    partner: true, fps: freezeObject({ allowed: freezeArray([30]), default: 30, generation: 30 })
  })
});

export const VIDEO_QUALITIES = Object.freeze({
  fast: freezeObject({
    id: 'fast',
    label: 'Fast',
    description: 'Fewer inference steps for quick iteration.'
  }),
  quality: freezeObject({
    id: 'quality',
    label: 'Quality',
    description: 'More inference steps for a higher-quality final clip.'
  }),
  balanced: freezeObject({ id: 'balanced', label: 'Balanced' }),
  turbo: freezeObject({ id: 'turbo', label: 'Turbo' }),
  twoStage: freezeObject({ id: 'twoStage', label: 'FastH3 Two-Stage' }),
  tenEros: freezeObject({ id: 'tenEros', label: '10Eros v1.4' }),
  mini: freezeObject({ id: 'mini', label: '2.0 Mini' }),
  latest: freezeObject({ id: 'latest', label: '2.5' }),
  enhanced: freezeObject({ id: 'enhanced', label: 'Enhanced' })
});

export const VIDEO_MODEL_IDS = Object.freeze({
  wan22: freezeObject({
    fast: 'wan_v2.2-14b-fp8_i2v_lightx2v',
    quality: 'wan_v2.2-14b-fp8_i2v'
  }),
  ltx23: freezeObject({
    fast: 'ltx23-22b-fp8_i2v_distilled',
    quality: 'ltx23-22b-fp8_i2v_dev',
    tenEros: 'ltx23-22b-10eros-v1.4-fp8mixed_i2v'
  }),
  ltx25: freezeObject({ fast: 'ltx25-22b-int8_i2v_distilled' }),
  minimax: freezeObject({ fast: 'minimax-h3-fastvideo-int8_i2v_turbo', turbo: 'minimax-h3-fl2va-fp8_i2v_turbo',
    balanced: 'minimax-h3-fl2va-fp8_i2v_balanced', quality: 'minimax-h3-fl2va-fp8_i2v',
    twoStage: 'minimax-h3-fastvideo-int8_i2v_turbo_2stage' }),
  seedance: freezeObject({ fast: 'seedance-2-0-fast', quality: 'seedance-2-0', mini: 'seedance-2-0-mini', latest: 'seedance-2-5' }),
  happyhorse: freezeObject({ quality: 'happyhorse-1.1-i2v' }),
  wan3: freezeObject({ quality: 'wan3.0-video', enhanced: 'wan3.0-spicy-video' })
});

function createModel({
  id,
  family,
  quality,
  steps,
  guidance,
  shift,
  sampler,
  scheduler,
  variant,
  description,
  ...extra
}) {
  const familyConfig = VIDEO_FAMILIES[family];
  const qualityConfig = VIDEO_QUALITIES[quality];

  return freezeObject({
    id,
    family,
    quality,
    steps,
    guidance,
    ...(shift !== undefined ? { shift } : {}),
    sampler,
    scheduler,
    variant,
    ...extra,
    partner: familyConfig.partner === true,
    label: `${familyConfig.label} · ${qualityConfig.label}`,
    familyLabel: familyConfig.label,
    qualityLabel: qualityConfig.label,
    description,
    durationLabel: `${familyConfig.durationSeconds.min}–${familyConfig.durationSeconds.max} seconds`,
    fpsLabel: familyConfig.fps.allowed.length === 1
      ? `${familyConfig.fps.allowed[0]} fps`
      : `${familyConfig.fps.allowed.join(' or ')} fps`,
    dimensionLabel: `${familyConfig.dimensions.min}–${familyConfig.dimensions.max}px in multiples of ${familyConfig.dimensions.divisor}`,
    supportsNativeAudio: familyConfig.supportsNativeAudio,
    defaultDurationSeconds: familyConfig.durationSeconds.default,
    defaultFps: familyConfig.fps.default
  });
}

const MODEL_LIST = [
  createModel({
    id: VIDEO_MODEL_IDS.wan22.fast,
    family: 'wan22',
    quality: 'fast',
    steps: 4,
    guidance: 1,
    shift: 5,
    sampler: 'euler',
    scheduler: 'simple',
    variant: 'LightX2V',
    description: 'Fast WAN 2.2 animation for previews and iteration.'
  }),
  createModel({
    id: VIDEO_MODEL_IDS.wan22.quality,
    family: 'wan22',
    quality: 'quality',
    steps: 20,
    guidance: 4,
    shift: 8,
    sampler: 'euler',
    scheduler: 'simple',
    variant: 'Base',
    description: 'Full WAN 2.2 model for higher-quality motion.'
  }),
  createModel({
    id: VIDEO_MODEL_IDS.ltx23.fast,
    family: 'ltx23',
    quality: 'fast',
    steps: 8,
    guidance: 1,
    sampler: 'euler_ancestral_cfg_pp',
    scheduler: 'manual_sigmas',
    variant: 'Distilled',
    description: 'Distilled LTX-2.3 animation with native synchronized audio.'
  }),
  createModel({
    id: VIDEO_MODEL_IDS.ltx23.quality,
    family: 'ltx23',
    quality: 'quality',
    steps: 30,
    guidance: 3,
    sampler: 'euler_ancestral_cfg_pp',
    scheduler: 'manual_sigmas',
    variant: 'Dev',
    description: 'LTX-2.3 Dev for higher-quality motion and native synchronized audio.'
  }),
  createModel({ id: VIDEO_MODEL_IDS.ltx23.tenEros, family: 'ltx23', quality: 'tenEros', steps: 9,
    guidance: 1, sampler: 'euler_ancestral', scheduler: 'manual_sigmas', variant: 'Community fine-tune' }),
  createModel({ id: VIDEO_MODEL_IDS.ltx25.fast, family: 'ltx25', quality: 'fast', steps: 8,
    guidance: 1, sampler: 'euler_ancestral', scheduler: 'manual_sigmas', variant: 'Distilled' }),
  ...Object.entries(VIDEO_MODEL_IDS.minimax).map(([quality, id]) => createModel({ id, family: 'minimax', quality,
    steps: quality === 'quality' ? 20 : quality === 'balanced' ? 8 : 4, guidance: 1,
    sampler: quality === 'quality' ? 'res_multistep' : 'euler', scheduler: 'simple',
    ...(quality === 'balanced' ? { shift: 6 } : {}),
    ...(quality === 'twoStage' ? { outputScale: 2 } : {}),
    variant: quality === 'fast' ? 'FastH3 Turbo' : quality === 'turbo' ? 'LightX2V Turbo'
      : quality === 'quality' ? 'Standard' : VIDEO_QUALITIES[quality].label })),
  ...Object.entries(VIDEO_MODEL_IDS.seedance).map(([quality, id]) => createModel({ id, family: 'seedance', quality,
    variant: quality === 'quality' ? '2.0' : quality === 'fast' ? '2.0 Fast' : VIDEO_QUALITIES[quality].label,
    ...(quality !== 'latest' ? { maxDurationSeconds: 15 } : {}) })),
  createModel({ id: VIDEO_MODEL_IDS.happyhorse.quality, family: 'happyhorse', quality: 'quality', variant: 'Image to video', nativeAudioAlwaysOn: true }),
  ...Object.entries(VIDEO_MODEL_IDS.wan3).map(([quality, id]) => createModel({ id, family: 'wan3', quality,
    variant: quality === 'quality' ? 'Standard' : 'Enhanced' }))
];

export const VIDEO_MODEL_OPTIONS = freezeArray(MODEL_LIST);

export const VIDEO_MODELS = Object.freeze(
  Object.fromEntries(VIDEO_MODEL_OPTIONS.map((model) => [model.id, model]))
);

function createResolution({ id, label, width, height, ratio, orientation }) {
  return freezeObject({
    id,
    label,
    width,
    height,
    ratio,
    orientation,
    dimensionsLabel: `${width} × ${height}`,
    displayLabel: `${label} · ${width} × ${height}`,
    aspect: width / height
  });
}

/**
 * Exact live Sogni size presets that are valid for both Wan 2.2 and LTX-2.3.
 * Every dimension is <=1536, divisible by 64 (and therefore by 16), and at
 * least LTX-2.3's 640px minimum.
 */
export const VIDEO_RESOLUTION_PRESETS = freezeArray([
  createResolution({
    id: 'square_768',
    label: 'Square',
    width: 768,
    height: 768,
    ratio: '1:1',
    orientation: 'square'
  }),
  createResolution({
    id: 'square_hd',
    label: 'Square HD',
    width: 1024,
    height: 1024,
    ratio: '1:1',
    orientation: 'square'
  }),
  createResolution({
    id: 'portrait_13_19',
    label: 'Portrait',
    width: 832,
    height: 1216,
    ratio: '13:19',
    orientation: 'portrait'
  }),
  createResolution({
    id: 'portrait_4_7',
    label: 'Tall Portrait',
    width: 768,
    height: 1344,
    ratio: '4:7',
    orientation: 'portrait'
  }),
  createResolution({
    id: 'landscape_19_13',
    label: 'Landscape',
    width: 1216,
    height: 832,
    ratio: '19:13',
    orientation: 'landscape'
  }),
  createResolution({
    id: 'landscape_7_4',
    label: 'Widescreen',
    width: 1344,
    height: 768,
    ratio: '7:4',
    orientation: 'landscape'
  }),
  createResolution({
    id: 'portrait_5_12',
    label: 'Vertical Ultrawide',
    width: 640,
    height: 1536,
    ratio: '5:12',
    orientation: 'portrait'
  }),
  createResolution({
    id: 'landscape_12_5',
    label: 'Cinematic Ultrawide',
    width: 1536,
    height: 640,
    ratio: '12:5',
    orientation: 'landscape'
  })
]);

export const VIDEO_RESOLUTIONS = Object.freeze(
  Object.fromEntries(VIDEO_RESOLUTION_PRESETS.map((resolution) => [resolution.id, resolution]))
);

export const DEFAULT_VIDEO_FAMILY = 'wan22';
export const DEFAULT_VIDEO_QUALITY = 'fast';
export const DEFAULT_VIDEO_RESOLUTION_ID = 'square_768';
export const DEFAULT_VIDEO_MODEL_ID = VIDEO_MODEL_IDS[DEFAULT_VIDEO_FAMILY][DEFAULT_VIDEO_QUALITY];

export function getVideoFamily(family) {
  if (typeof family !== 'string' || !hasOwn(VIDEO_FAMILIES, family)) {
    fail(
      'family',
      `Unknown video family "${String(family)}". Expected one of: ${Object.keys(VIDEO_FAMILIES).join(', ')}.`,
      'unknown_video_family'
    );
  }
  return VIDEO_FAMILIES[family];
}

export function getVideoQuality(quality) {
  if (typeof quality !== 'string' || !hasOwn(VIDEO_QUALITIES, quality)) {
    fail(
      'quality',
      `Unknown video quality "${String(quality)}". Expected one of: ${Object.keys(VIDEO_QUALITIES).join(', ')}.`,
      'unknown_video_quality'
    );
  }
  return VIDEO_QUALITIES[quality];
}

export function getVideoModel(modelId, catalogModel) {
  if (typeof modelId !== 'string' || !hasOwn(VIDEO_MODELS, modelId)) {
    fail(
      'modelId',
      `Unknown video model "${String(modelId)}".`,
      'unknown_video_model'
    );
  }
  const model = VIDEO_MODELS[modelId];
  if (!catalogModel) return model;
  const params = catalogModel.parameters;
  return freezeObject({ ...model,
    ...(params.steps ? { steps: params.steps.default } : {}),
    ...(params.guidance ? { guidance: params.guidance.default } : {}),
    ...(params.shift ? { shift: params.shift.default } : {}),
    ...(params.strength ? { strength: params.strength.default } : {}),
    ...(params.comfySampler ? { sampler: params.comfySampler.default } : {}),
    ...(params.comfyScheduler ? { scheduler: params.comfyScheduler.default } : {}),
    parameters: params, name: catalogModel.name, availableNetworks: catalogModel.availableNetworks,
    maxPromptLength: params.maxPromptLength || 2000 });
}

export function getVideoModelId(family, quality) {
  getVideoFamily(family);
  getVideoQuality(quality);
  const id = VIDEO_MODEL_IDS[family][quality];
  if (!id) fail('quality', `Choose a supported variant for ${VIDEO_FAMILIES[family].label}.`, 'unsupported_video_variant');
  return id;
}

/**
 * Resolve a model from a canonical model ID or a family/quality pair. When
 * redundant selection fields are supplied they must agree.
 */
export function resolveVideoModel(selection) {
  if (!selection || typeof selection !== 'object' || Array.isArray(selection)) {
    fail('modelId', 'A video model selection is required.', 'missing_video_model');
  }

  const { modelId, family, quality } = selection;
  const model = modelId !== undefined
    ? getVideoModel(modelId, selection.catalogModel)
    : getVideoModel(getVideoModelId(family, quality));

  if (family !== undefined) {
    getVideoFamily(family);
    if (model.family !== family) {
      fail(
        'family',
        `Video model ${model.id} belongs to ${model.family}, not ${family}.`,
        'video_model_selection_mismatch'
      );
    }
  }

  if (quality !== undefined) {
    getVideoQuality(quality);
    if (model.quality !== quality) {
      fail(
        'quality',
        `Video model ${model.id} is the ${model.quality} variant, not ${quality}.`,
        'video_model_selection_mismatch'
      );
    }
  }

  return model;
}

export function getVideoResolution(resolutionId) {
  if (typeof resolutionId !== 'string' || !hasOwn(VIDEO_RESOLUTIONS, resolutionId)) {
    fail(
      'resolutionId',
      `Unknown video resolution "${String(resolutionId)}".`,
      'unknown_video_resolution'
    );
  }
  return VIDEO_RESOLUTIONS[resolutionId];
}

export function findVideoResolution(width, height) {
  return VIDEO_RESOLUTION_PRESETS.find(
    (resolution) => resolution.width === width && resolution.height === height
  ) || null;
}

export function getVideoResolutionByDimensions(width, height) {
  const resolution = findVideoResolution(width, height);
  if (!resolution) {
    fail(
      'resolutionId',
      `No named video resolution matches ${String(width)} × ${String(height)}.`,
      'unknown_video_resolution'
    );
  }
  return resolution;
}

function requireFiniteNumber(value, field, label, { integer = false } = {}) {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    fail(field, `${label} must be a finite number.`, 'invalid_video_number');
  }
  if (integer && !Number.isInteger(value)) {
    fail(field, `${label} must be an integer.`, 'invalid_video_number');
  }
  return value;
}

function validateDimensionsForModel(model, width, height) {
  requireFiniteNumber(width, 'width', 'Video width', { integer: true });
  requireFiniteNumber(height, 'height', 'Video height', { integer: true });

  const defaults = VIDEO_FAMILIES[model.family].dimensions;
  const params = model.parameters;
  for (const [field, value] of [['width', width], ['height', height]]) {
    const { min, max } = params?.[field] || defaults;
    const divisor = model.partner ? 1 : params?.[field]?.step || defaults.divisor;
    if (value < min || value > max) {
      fail(
        field,
        `Video ${field} for ${model.familyLabel} must be between ${min} and ${max}px.`,
        'video_dimension_out_of_range'
      );
    }
    if (value % divisor !== 0) {
      fail(
        field,
        `Video ${field} for ${model.familyLabel} must be divisible by ${divisor}.`,
        'video_dimension_not_divisible'
      );
    }
  }

  const maxPixels = params?.maxPixels || VIDEO_FAMILIES[model.family].maxPixels
    || (model.family === 'wan22' ? 1048576 : null);
  if (maxPixels && width * height > maxPixels) {
    fail('width', `This variant supports at most ${maxPixels.toLocaleString()} canvas pixels. Choose a smaller size.`, 'video_area_out_of_range');
  }

  return { width, height };
}

export function validateVideoDimensions({ modelId, family, quality, width, height } = {}) {
  const model = resolveVideoModel({ modelId, family, quality });
  return validateDimensionsForModel(model, width, height);
}

function validateTimingForModel(model, durationSeconds, fps) {
  requireFiniteNumber(durationSeconds, 'durationSeconds', 'Video duration');
  requireFiniteNumber(fps, 'fps', 'Video fps', { integer: true });

  const family = VIDEO_FAMILIES[model.family];
  const { min } = family.durationSeconds;
  const max = model.maxDurationSeconds || family.durationSeconds.max;
  if (durationSeconds < min || durationSeconds > max) {
    fail(
      'durationSeconds',
      `Video duration for ${model.familyLabel} must be between ${min} and ${max} seconds.`,
      'video_duration_out_of_range'
    );
  }

  // The Gallery exposes whole-second clips and the installed SDK derives its
  // frame count from an integer duration. Reject fractional values instead of
  // reporting timing metadata that can differ from the actual render.
  if (!Number.isInteger(durationSeconds)) {
    fail(
      'durationSeconds',
      'Video duration must be a whole number of seconds.',
      'invalid_video_number'
    );
  }

  if (!family.fps.allowed.includes(fps)) {
    fail(
      'fps',
      `Video fps for ${model.familyLabel} must be ${family.fps.allowed.join(' or ')}.`,
      'unsupported_video_fps'
    );
  }

  return { durationSeconds, fps };
}

export function validateVideoTiming({ modelId, family, quality, durationSeconds, fps } = {}) {
  const model = resolveVideoModel({ modelId, family, quality });
  return validateTimingForModel(model, durationSeconds, fps);
}

/**
 * Calculate the generation-frame count sent to the Sogni SDK.
 *
 * Wan always generates at 16 fps; 32 fps is post-render interpolation. LTX
 * generates natively at 24 fps and requires frame counts in the form 1+n*8.
 */
export function calculateVideoFrames(modelId, durationSeconds, fps) {
  const model = getVideoModel(modelId);
  validateTimingForModel(model, durationSeconds, fps);
  const family = VIDEO_FAMILIES[model.family];

  if (model.family === 'wan22') {
    return Math.round(durationSeconds * family.fps.generation) + 1;
  }

  if (model.family === 'minimax') {
    return 124 + Math.max(0, Math.min(14, Math.round((Math.round(durationSeconds * 24) - 124) / 17))) * 17;
  }

  if (model.partner) return Math.round(durationSeconds * family.fps.generation) + 1;

  const frameIntervals = Math.round(
    (durationSeconds * family.fps.generation) / family.frames.step
  ) * family.frames.step;
  return frameIntervals + 1;
}

export function getVideoTiming(modelId, durationSeconds, fps) {
  const model = getVideoModel(modelId);
  const generationFrames = calculateVideoFrames(modelId, durationSeconds, fps);
  const family = VIDEO_FAMILIES[model.family];
  const actualDurationSeconds = model.family === 'minimax'
    ? generationFrames / family.fps.generation : (generationFrames - 1) / family.fps.generation;
  const outputFrames = model.family === 'minimax' ? generationFrames : Math.round(actualDurationSeconds * fps) + 1;

  return freezeObject({
    requestedDurationSeconds: durationSeconds,
    actualDurationSeconds,
    generationFps: family.fps.generation,
    outputFps: fps,
    generationFrames,
    outputFrames,
    interpolated: fps !== family.fps.generation
  });
}

/**
 * Strictly validate and resolve an API/UI video request. A named resolution may
 * provide width/height; if both forms are supplied they must match exactly.
 */
export function validateVideoRequest(request = {}) {
  if (!request || typeof request !== 'object' || Array.isArray(request)) {
    fail('request', 'Video request must be an object.', 'invalid_video_request');
  }

  const model = resolveVideoModel(request);
  const { durationSeconds, fps, resolutionId } = request;
  validateTimingForModel(model, durationSeconds, fps);

  let { width, height } = request;
  let resolution = null;
  if (resolutionId !== undefined) {
    resolution = getVideoResolution(resolutionId);
    if (width === undefined && height === undefined) {
      width = resolution.width;
      height = resolution.height;
    } else if (width !== resolution.width || height !== resolution.height) {
      fail(
        'resolutionId',
        `Resolution ${resolutionId} requires ${resolution.width} × ${resolution.height}.`,
        'video_resolution_mismatch'
      );
    }
  }

  if ((width === undefined) !== (height === undefined)) {
    fail(
      width === undefined ? 'width' : 'height',
      'Video width and height must be provided together.',
      'incomplete_video_dimensions'
    );
  }

  validateDimensionsForModel(model, width, height);
  const timing = getVideoTiming(model.id, durationSeconds, fps);

  return freezeObject({
    modelId: model.id,
    family: model.family,
    quality: model.quality,
    durationSeconds,
    fps,
    width,
    height,
    resolutionId: resolution?.id || findVideoResolution(width, height)?.id || null,
    frames: timing.generationFrames,
    timing,
    model
  });
}

/** Return family-aware defaults without applying them to an existing request. */
export function getVideoDefaults({
  family = DEFAULT_VIDEO_FAMILY,
  quality = DEFAULT_VIDEO_QUALITY,
  resolutionId = DEFAULT_VIDEO_RESOLUTION_ID
} = {}) {
  const modelId = getVideoModelId(family, quality);
  const familyConfig = VIDEO_FAMILIES[family];
  const resolution = getVideoResolution(resolutionId);

  return validateVideoRequest({
    modelId,
    family,
    quality,
    durationSeconds: familyConfig.durationSeconds.default,
    fps: familyConfig.fps.default,
    resolutionId,
    width: resolution.width,
    height: resolution.height
  });
}

export const VIDEO_DEFAULTS = getVideoDefaults();
